import type { z } from 'zod';
import {
  BROWSER_AUTOMATION_TOOL_NAME,
  INTERCEPT_TOGGLE_TOOL_NAME,
  INTERCEPT_RULE_TOOL_NAME,
  LAUNCH_BROWSER_TOOL_NAME,
  MATCH_REPLACE_RULE_TOOL_NAME,
  PROXY_CONTROL_TOOL_NAME,
  PROXY_HISTORY_TOOL_NAME,
  REPLAY_REQUEST_TOOL_NAME,
  TRACK_HISTORY_REQUEST_TOOL_NAME,
  MCP_OAUTH_AUTHORIZE_TOOL_NAME,
  MCP_CREDENTIAL_DIALOG_TOOL_NAME,
  type PortalToolName,
} from './portal-tool.constants';
import { BrowserAutomationInputSchema } from './browser-automation.schema';
import { InterceptToggleInputSchema } from './intercept-toggle.schema';
import type { InterceptRuleInputSchema } from './intercept-rule.schema';
import { InterceptRuleToolInputSchema } from './intercept-rule.schema';
import { LaunchBrowserInputSchema } from './launch-browser.schema';
import type { MatchReplaceRuleInputSchema } from './match-replace-rule.schema';
import { MatchReplaceRuleToolInputSchema } from './match-replace-rule.schema';
import { ProxyControlInputSchema } from './proxy-control.schema';
import { ProxyHistoryInputSchema } from './proxy-history.schema';
import { ReplayFromHistoryToolInputSchema } from './replay-request.schema';
import { TrackHistoryRequestInputSchema } from './track-history-request.schema';
import { McpOAuthAuthorizeInputSchema } from './mcp-oauth-authorize.schema';
import { McpCredentialDialogInputSchema } from './mcp-credential-dialog.schema';

export type PortalToolDisplayInputFormatter = (
  input: unknown,
) => string | undefined;

export type PortalToolPayloadFormatter = (
  args: Record<string, unknown>,
) => string | null;

function truncateValue({
  value,
  maxLength = 50,
}: {
  value: string;
  maxLength?: number;
}): string {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength - 3)}...`;
}

function formatActionDisplayInput<T extends { action: string }>({
  schema,
  input,
}: {
  schema: z.ZodType<T>;
  input: unknown;
}): string | undefined {
  const parsed = schema.safeParse(input);
  return parsed.success ? parsed.data.action : undefined;
}

export function formatProxyControlDisplayInput(
  input: unknown,
): string | undefined {
  return formatActionDisplayInput({
    schema: ProxyControlInputSchema,
    input,
  });
}

export function formatInterceptToggleDisplayInput(
  input: unknown,
): string | undefined {
  return formatActionDisplayInput({
    schema: InterceptToggleInputSchema,
    input,
  });
}

export function formatInterceptRuleDisplayInput(
  input: unknown,
): string | undefined {
  return formatActionDisplayInput({
    schema: InterceptRuleToolInputSchema,
    input,
  });
}

export function formatMatchReplaceRuleDisplayInput(
  input: unknown,
): string | undefined {
  return formatActionDisplayInput({
    schema: MatchReplaceRuleToolInputSchema,
    input,
  });
}

export function formatReplayRequestDisplayInput(
  input: unknown,
): string | undefined {
  const parsed = ReplayFromHistoryToolInputSchema.safeParse(input);
  if (!parsed.success) {
    return undefined;
  }

  return `from-history: ${parsed.data.requestId}`;
}

export function formatLaunchBrowserDisplayInput(
  input: unknown,
): string | undefined {
  const parsed = LaunchBrowserInputSchema.safeParse(input);
  if (!parsed.success) {
    return undefined;
  }

  if (parsed.data.action !== 'launch') {
    return parsed.data.action;
  }

  if (parsed.data.instanceId) {
    return parsed.data.startUrl ? `tab: ${parsed.data.startUrl}` : 'open tab';
  }

  return parsed.data.startUrl;
}

export function formatProxyHistoryDisplayInput(
  input: unknown,
): string | undefined {
  return formatActionDisplayInput({
    schema: ProxyHistoryInputSchema,
    input,
  });
}

function formatBrowserAutomationStepLabel(
  input: z.infer<typeof BrowserAutomationInputSchema>,
): string {
  const { action, elementIndex, scrollDirection, waitMs } = input;

  switch (action) {
    case 'navigate':
      return 'Navigate';
    case 'click':
      return elementIndex !== undefined ? `Click #${elementIndex}` : 'Click';
    case 'type':
      return elementIndex !== undefined ? `Type #${elementIndex}` : 'Type';
    case 'scroll':
      return scrollDirection ? `Scroll ${scrollDirection}` : 'Scroll';
    case 'wait':
      return waitMs !== undefined ? `Wait ${waitMs}ms` : 'Wait';
    case 'get-dom-state':
      return 'Get DOM state';
    case 'screenshot':
      return 'Screenshot';
    case 'list-alerts':
      return 'List alerts';
    case 'clear-alerts':
      return 'Clear alerts';
    case 'get-storage':
      return 'Get storage';
    case 'evaluate-js':
      return 'Evaluate JS';
    case 'complete':
      return 'Complete';
    default:
      return action;
  }
}

export function formatBrowserAutomationActionLabel(input: unknown): string {
  const parsed = BrowserAutomationInputSchema.safeParse(input);
  if (!parsed.success) {
    return 'Browser: step';
  }

  return `Browser: ${formatBrowserAutomationStepLabel(parsed.data)}`;
}

export function formatBrowserAutomationDisplayInput(
  input: unknown,
): string | undefined {
  const parsed = BrowserAutomationInputSchema.safeParse(input);
  if (!parsed.success) {
    return undefined;
  }

  return formatBrowserAutomationActionLabel(parsed.data);
}

export function formatProxyControlPayload(
  args: Record<string, unknown>,
): string | null {
  const parsed = ProxyControlInputSchema.safeParse(args);
  if (!parsed.success) {
    return null;
  }

  return `**Action:** \`${parsed.data.action}\``;
}

export function formatInterceptTogglePayload(
  args: Record<string, unknown>,
): string | null {
  const parsed = InterceptToggleInputSchema.safeParse(args);
  if (!parsed.success) {
    return null;
  }

  const lines = [`**Action:** \`${parsed.data.action}\``];
  if (parsed.data.queueItemId) {
    lines.push(`**Queue item:** \`${parsed.data.queueItemId}\``);
  }
  return lines.join('\n');
}

function formatMatchReplaceRuleDetails(
  rule: z.infer<typeof MatchReplaceRuleInputSchema>,
): string[] {
  const lines = [
    `**Name:** ${rule.name}`,
    `**Match:** ${rule.matchType} in ${rule.matchLocation}`,
    `**Pattern:** \`${rule.matchPattern}\``,
  ];

  if (rule.headerName) {
    lines.push(`**Header:** \`${rule.headerName}\``);
  }

  lines.push(`**Replace with:** \`${rule.replaceWith}\``);

  if (rule.isRegex) {
    lines.push('**Regex:** yes');
  }

  if (rule.enabled === false) {
    lines.push('**Enabled:** no');
  }

  return lines;
}

export function formatMatchReplaceRulePayload(
  args: Record<string, unknown>,
): string | null {
  const parsed = MatchReplaceRuleToolInputSchema.safeParse(args);
  if (!parsed.success) {
    return null;
  }

  const lines = [`**Action:** \`${parsed.data.action}\``];

  if (parsed.data.ruleId) {
    lines.push(`**Rule ID:** \`${parsed.data.ruleId}\``);
  }

  if (parsed.data.nameFilter) {
    lines.push(`**Name filter:** \`${parsed.data.nameFilter}\``);
  }

  if (parsed.data.rule) {
    lines.push('', ...formatMatchReplaceRuleDetails(parsed.data.rule));
  }

  return lines.join('\n');
}

function formatInterceptRuleDetails(
  rule: z.infer<typeof InterceptRuleInputSchema>,
): string[] {
  const lines = [
    `**Name:** ${rule.name}`,
    `**URL pattern:** \`${rule.urlPattern}\``,
  ];

  if (rule.methods && rule.methods.length > 0) {
    lines.push(`**Methods:** ${rule.methods.join(', ')}`);
  }

  if (rule.enabled === false) {
    lines.push('**Enabled:** no');
  }

  if (rule.description) {
    lines.push(`**Description:** ${rule.description}`);
  }

  return lines;
}

export function formatInterceptRulePayload(
  args: Record<string, unknown>,
): string | null {
  const parsed = InterceptRuleToolInputSchema.safeParse(args);
  if (!parsed.success) {
    return null;
  }

  const lines = [`**Action:** \`${parsed.data.action}\``];

  if (parsed.data.ruleId) {
    lines.push(`**Rule ID:** \`${parsed.data.ruleId}\``);
  }

  if (parsed.data.nameFilter) {
    lines.push(`**Name filter:** \`${parsed.data.nameFilter}\``);
  }

  if (parsed.data.rule) {
    lines.push('', ...formatInterceptRuleDetails(parsed.data.rule));
  }

  return lines.join('\n');
}

export function formatLaunchBrowserPayload(
  args: Record<string, unknown>,
): string | null {
  const parsed = LaunchBrowserInputSchema.safeParse(args);
  if (!parsed.success) {
    return null;
  }

  const lines = [`**Action:** \`${parsed.data.action}\``];

  if (parsed.data.startUrl) {
    lines.push(`**Start URL:** \`${parsed.data.startUrl}\``);
  }

  if (parsed.data.instanceId) {
    lines.push(`**Instance:** \`${parsed.data.instanceId}\``);
  }

  if (parsed.data.credentialId) {
    lines.push(`**Credential:** \`${parsed.data.credentialId}\``);
  }

  if (parsed.data.nameFilter) {
    lines.push(`**Name filter:** \`${parsed.data.nameFilter}\``);
  }

  if (parsed.data.freshSession !== undefined) {
    lines.push(`**Fresh session:** ${parsed.data.freshSession ? 'yes' : 'no'}`);
  }

  return lines.length > 1 ? lines.join('\n') : null;
}

export function formatReplayRequestPayload(
  args: Record<string, unknown>,
): string | null {
  const parsed = ReplayFromHistoryToolInputSchema.safeParse(args);
  if (!parsed.success) {
    return null;
  }

  const lines = [
    `**Action:** \`from-history\``,
    `**Request ID:** \`${parsed.data.requestId}\``,
  ];

  if (parsed.data.protocol) {
    lines.push(`**Protocol:** \`${parsed.data.protocol}\``);
  }

  if (parsed.data.followRedirects) {
    lines.push('**Follow redirects:** yes');
  }

  return lines.join('\n');
}

export function formatBrowserAutomationPayload(
  args: Record<string, unknown>,
): string | null {
  const parsed = BrowserAutomationInputSchema.safeParse(args);
  if (!parsed.success) {
    return null;
  }

  const lines = [
    `**Action:** \`${parsed.data.action}\``,
    `**Instance:** \`${parsed.data.instanceId}\``,
  ];

  if (parsed.data.pageId) {
    lines.push(`**Page:** \`${parsed.data.pageId}\``);
  }

  if (parsed.data.url) {
    lines.push(`**URL:** \`${parsed.data.url}\``);
  }

  if (parsed.data.elementIndex !== undefined) {
    lines.push(`**Element:** \`${parsed.data.elementIndex}\``);
  }

  if (parsed.data.text) {
    lines.push(
      `**Text:** \`${truncateValue({ value: parsed.data.text, maxLength: 80 })}\``,
    );
  }

  if (parsed.data.scrollDirection) {
    lines.push(`**Scroll:** \`${parsed.data.scrollDirection}\``);
  }

  if (parsed.data.waitMs !== undefined) {
    lines.push(`**Wait:** ${parsed.data.waitMs}ms`);
  }

  if (parsed.data.completionMessage) {
    lines.push(
      `**Completion:** \`${truncateValue({ value: parsed.data.completionMessage, maxLength: 120 })}\``,
    );
  }

  if (parsed.data.script) {
    lines.push(
      `**Script:** \`${truncateValue({ value: parsed.data.script, maxLength: 120 })}\``,
    );
  }

  return lines.join('\n');
}

export function formatProxyHistoryPayload(
  args: Record<string, unknown>,
): string | null {
  const parsed = ProxyHistoryInputSchema.safeParse(args);
  if (!parsed.success) {
    return null;
  }

  const lines = [`**Action:** \`${parsed.data.action}\``];

  if (parsed.data.requestId) {
    lines.push(`**Request ID:** \`${parsed.data.requestId}\``);
  }

  if (parsed.data.action === 'list') {
    lines.push(`**Skip:** ${parsed.data.skip}`);
    lines.push(`**Limit:** ${parsed.data.limit}`);
  }

  if (parsed.data.host) {
    lines.push(`**Host:** \`${parsed.data.host}\``);
  }

  if (parsed.data.method) {
    lines.push(`**Method:** \`${parsed.data.method}\``);
  }

  if (parsed.data.search) {
    lines.push(`**Search:** \`${parsed.data.search}\``);
  }

  return lines.join('\n');
}

function formatTrackHistoryRequestDisplayInput(
  input: unknown,
): string | undefined {
  const parsed = TrackHistoryRequestInputSchema.safeParse(input);
  if (!parsed.success) {
    return undefined;
  }

  const lines = [`**Request ID:** \`${parsed.data.requestId}\``];
  if (parsed.data.credentialId) {
    lines.push(`**Credential ID:** \`${parsed.data.credentialId}\``);
  }

  return lines.join('\n');
}

export function formatTrackHistoryRequestPayload(
  args: Record<string, unknown>,
): string | null {
  const parsed = TrackHistoryRequestInputSchema.safeParse(args);
  if (!parsed.success) {
    return null;
  }

  const lines = [`**Request ID:** \`${parsed.data.requestId}\``];
  if (parsed.data.credentialId) {
    lines.push(`**Credential ID:** \`${parsed.data.credentialId}\``);
  }

  return lines.join('\n');
}

function formatMcpOAuthAuthorizeDisplayInput(
  input: unknown,
): string | undefined {
  const parsed = McpOAuthAuthorizeInputSchema.safeParse(input);
  if (!parsed.success) {
    return undefined;
  }

  return parsed.data.identityId
    ? `**Identity ID:** \`${parsed.data.identityId}\``
    : 'Authorize default MCP identity';
}

function formatMcpOAuthAuthorizePayload(
  args: Record<string, unknown>,
): string | null {
  const parsed = McpOAuthAuthorizeInputSchema.safeParse(args);
  if (!parsed.success) {
    return null;
  }

  return parsed.data.identityId
    ? `**Identity ID:** \`${parsed.data.identityId}\``
    : 'Authorize default MCP identity';
}

function formatMcpCredentialDialogDisplayInput(
  input: unknown,
): string | undefined {
  const parsed = McpCredentialDialogInputSchema.safeParse(input);
  if (!parsed.success) {
    return undefined;
  }

  return parsed.data.identityId
    ? `**Identity ID:** \`${parsed.data.identityId}\``
    : 'Open MCP credential dialog';
}

function formatMcpCredentialDialogPayload(
  args: Record<string, unknown>,
): string | null {
  const parsed = McpCredentialDialogInputSchema.safeParse(args);
  if (!parsed.success) {
    return null;
  }

  return parsed.data.identityId
    ? `**Identity ID:** \`${parsed.data.identityId}\``
    : 'Open MCP credential dialog';
}

const PORTAL_TOOL_DISPLAY_INPUT_FORMATTERS: Record<
  PortalToolName,
  PortalToolDisplayInputFormatter
> = {
  [PROXY_CONTROL_TOOL_NAME]: formatProxyControlDisplayInput,
  [INTERCEPT_TOGGLE_TOOL_NAME]: formatInterceptToggleDisplayInput,
  [INTERCEPT_RULE_TOOL_NAME]: formatInterceptRuleDisplayInput,
  [MATCH_REPLACE_RULE_TOOL_NAME]: formatMatchReplaceRuleDisplayInput,
  [REPLAY_REQUEST_TOOL_NAME]: formatReplayRequestDisplayInput,
  [LAUNCH_BROWSER_TOOL_NAME]: formatLaunchBrowserDisplayInput,
  [PROXY_HISTORY_TOOL_NAME]: formatProxyHistoryDisplayInput,
  [BROWSER_AUTOMATION_TOOL_NAME]: formatBrowserAutomationDisplayInput,
  [TRACK_HISTORY_REQUEST_TOOL_NAME]: formatTrackHistoryRequestDisplayInput,
  [MCP_OAUTH_AUTHORIZE_TOOL_NAME]: formatMcpOAuthAuthorizeDisplayInput,
  [MCP_CREDENTIAL_DIALOG_TOOL_NAME]: formatMcpCredentialDialogDisplayInput,
};

const PORTAL_TOOL_PAYLOAD_FORMATTERS: Record<
  PortalToolName,
  PortalToolPayloadFormatter
> = {
  [PROXY_CONTROL_TOOL_NAME]: formatProxyControlPayload,
  [INTERCEPT_TOGGLE_TOOL_NAME]: formatInterceptTogglePayload,
  [INTERCEPT_RULE_TOOL_NAME]: formatInterceptRulePayload,
  [MATCH_REPLACE_RULE_TOOL_NAME]: formatMatchReplaceRulePayload,
  [REPLAY_REQUEST_TOOL_NAME]: formatReplayRequestPayload,
  [LAUNCH_BROWSER_TOOL_NAME]: formatLaunchBrowserPayload,
  [PROXY_HISTORY_TOOL_NAME]: formatProxyHistoryPayload,
  [BROWSER_AUTOMATION_TOOL_NAME]: formatBrowserAutomationPayload,
  [TRACK_HISTORY_REQUEST_TOOL_NAME]: formatTrackHistoryRequestPayload,
  [MCP_OAUTH_AUTHORIZE_TOOL_NAME]: formatMcpOAuthAuthorizePayload,
  [MCP_CREDENTIAL_DIALOG_TOOL_NAME]: formatMcpCredentialDialogPayload,
};

export function createPortalToolDisplayInputFormatter(
  toolName: PortalToolName,
): PortalToolDisplayInputFormatter {
  return PORTAL_TOOL_DISPLAY_INPUT_FORMATTERS[toolName];
}

export function createPortalToolPayloadFormatter(
  toolName: PortalToolName,
): PortalToolPayloadFormatter {
  return PORTAL_TOOL_PAYLOAD_FORMATTERS[toolName];
}

export function formatPortalToolDisplayInput({
  toolName,
  input,
}: {
  toolName: PortalToolName;
  input: unknown;
}): string | undefined {
  return createPortalToolDisplayInputFormatter(toolName)(input);
}

export function formatPortalToolPayloadForTool({
  toolName,
  args,
}: {
  toolName: PortalToolName;
  args: Record<string, unknown>;
}): string | null {
  return createPortalToolPayloadFormatter(toolName)(args);
}
